import { createSavedReport, updateSavedReport } from '@api/savedReports';
import type { OfferSaveReportPayload } from '@diagon-alley/common';
import {
    Button,
    Group,
    Popover,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { BookmarkSimple, Check } from '@phosphor-icons/react';
import { extractApiErrorMessage } from '@utils/api-error';
import { useState } from 'react';

import { useReportTurn } from './reportTurn';
import styles from './ReportsContainer.module.css';

interface SaveReportRowProps {
    payload: OfferSaveReportPayload;
    onSaved?: () => void;
}

/**
 * Slim text-link row for the "save this report" action; click opens a
 * popover. Once saved, the link becomes a quiet "Saved as" / "Updated"
 * indicator so the same result is not saved twice.
 *
 * When the conversation is editing a saved report (opened from the library,
 * or saved earlier in the chat), the default is to update that report in
 * place: same id, so its schedules and links run the new version. Saving a
 * copy is the explicit second choice.
 */
export default function SaveReportRow({
    payload,
    onSaved,
}: SaveReportRowProps) {
    const [opened, setOpened] = useState(false);
    const [name, setName] = useState(payload.suggestedName ?? '');
    const [description, setDescription] = useState('');
    const [busy, setBusy] = useState(false);
    const [savedAs, setSavedAs] = useState<string | null>(null);
    const [updated, setUpdated] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { setSavedReport, editingReport } = useReportTurn();
    // Editing a saved report: the popover offers Update first; "Save as new" opens the name form.
    const [asNew, setAsNew] = useState(false);
    const isUpdate = !!editingReport && !asNew;

    const recipe =
        payload.recipeKind === 'freeform'
            ? { recipeKind: 'freeform' as const, freeformSpec: payload.freeformSpec }
            : {
                  recipeKind: 'template' as const,
                  templateId: payload.templateId,
                  templateVersion: payload.version,
                  params: payload.params,
              };

    const onUpdate = async () => {
        if (!editingReport || busy || savedAs) return;
        setBusy(true);
        setError(null);
        try {
            const saved = await updateSavedReport(editingReport.id, recipe);
            setSavedAs(saved.name);
            setUpdated(true);
            setSavedReport({ id: saved.id, name: saved.name });
            setOpened(false);
            onSaved?.();
        } catch (err) {
            setError(extractApiErrorMessage(err, 'Could not update the report. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        const trimmed = name.trim();
        if (!trimmed || busy || savedAs) return;
        setBusy(true);
        setError(null);
        try {
            const saved = await createSavedReport({
                name: trimmed,
                description: description.trim() || null,
                ...recipe,
            });
            setSavedAs(saved.name);
            setSavedReport({ id: saved.id, name: saved.name });
            setOpened(false);
            onSaved?.();
        } catch (err) {
            setError(
                extractApiErrorMessage(
                    err,
                    'Could not save the report. Please try again.',
                ),
            );
        } finally {
            setBusy(false);
        }
    };

    // Persistent saved indicator — once saved, the action is no longer
    // a button. We display "Saved as <name>" as a quiet confirmation
    // so the user can see exactly what landed in their library.
    if (savedAs) {
        return (
            <div className={styles.actionBar}>
                <span className={`${styles.actionLink} ${styles.actionLinkSaved}`}>
                    <Check size={14} weight="bold" />
                    <span>
                        {updated ? 'Updated' : 'Saved as'} &ldquo;{savedAs}&rdquo;
                    </span>
                </span>
            </div>
        );
    }

    return (
        <div className={styles.actionBar}>
            <Popover
                opened={opened}
                onChange={setOpened}
                width={300}
                position="top-start"
                shadow="md"
                withArrow
                arrowSize={10}
                trapFocus
                closeOnEscape
                closeOnClickOutside
            >
                <Popover.Target>
                    <button
                        type="button"
                        className={styles.actionLink}
                        onClick={() => setOpened((o) => !o)}
                    >
                        <BookmarkSimple size={14} weight="bold" />
                        <span>{editingReport ? `Save changes to “${editingReport.name}”` : 'Save report'}</span>
                    </button>
                </Popover.Target>
                <Popover.Dropdown p="sm">
                    {isUpdate ? (
                        <Stack gap="xs">
                            <Text size="xs" c="dimmed">
                                Replace &ldquo;{editingReport.name}&rdquo; with this version. Its schedules and
                                links will run it from now on.
                            </Text>
                            {error && (
                                <Text size="xs" c="red">
                                    {error}
                                </Text>
                            )}
                            <Group justify="flex-end" gap="xs">
                                <Button size="xs" variant="subtle" color="gray" onClick={() => setAsNew(true)} disabled={busy}>
                                    Save as new
                                </Button>
                                <Button size="xs" onClick={onUpdate} loading={busy}>
                                    Update
                                </Button>
                            </Group>
                        </Stack>
                    ) : (
                    <Stack gap="xs">
                        <Text size="xs" c="dimmed">
                            Save to your reports library for one-click rerun later.
                        </Text>
                        <TextInput
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            placeholder="Give it a short name"
                            disabled={busy}
                            size="xs"
                            autoFocus
                        />
                        <Textarea
                            label="Description (optional)"
                            value={description}
                            onChange={(e) =>
                                setDescription(e.currentTarget.value)
                            }
                            placeholder="What does this report tell you?"
                            disabled={busy}
                            size="xs"
                            autosize
                            minRows={2}
                            maxRows={4}
                        />
                        {error && (
                            <Text size="xs" c="red">
                                {error}
                            </Text>
                        )}
                        <Group justify="flex-end" gap="xs">
                            <Button
                                size="xs"
                                variant="subtle"
                                color="gray"
                                onClick={() => (editingReport ? setAsNew(false) : setOpened(false))}
                                disabled={busy}
                            >
                                {editingReport ? 'Back' : 'Cancel'}
                            </Button>
                            <Button
                                size="xs"
                                onClick={onSave}
                                disabled={!name.trim() || busy}
                                loading={busy}
                            >
                                Save
                            </Button>
                        </Group>
                    </Stack>
                    )}
                </Popover.Dropdown>
            </Popover>
        </div>
    );
}
